<script setup lang="ts">
/**
 * SMS billing editor: aggregator_id → sms_type → { steps, packages }.
 * Per aggregator: pick SMS types, then each type gets its own step and package grids.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxTagBox } from 'devextreme-vue/tag-box';
import { DxTabs } from 'devextreme-vue/tabs';
import {
  DxDataGrid,
  DxColumn,
  DxEditing,
} from 'devextreme-vue/data-grid';
import { LookupBox, bottoGridPaging, getAggregatorsLookup, getMessageTypesLookup } from '@botto/core';
import {
  emptySmsTypeBillingUi,
  emptySmsTypePrices,
  aggregatorSmsTypeMapToUi,
  uiToAggregatorSmsTypeMap,
  type AggregatorSmsBillingUi,
  type SmsTypeBillingUiState,
  type SmsTypePricesUi,
} from '../../api/normalizers';
import type { AggregatorSmsTypeMap } from '../../api/types';

const { paging, pager } = bottoGridPaging({ pageSize: 10 });

const props = defineProps<{
  modelValue: AggregatorSmsTypeMap | undefined | null;
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: AggregatorSmsTypeMap): void;
}>();

const ui = ref<SmsTypeBillingUiState>(emptySmsTypeBillingUi());
const currentBlockIndex = ref(0);
const newAggregatorId = ref<number | null>(null);
const aggregatorsLookup = getAggregatorsLookup();
const messageTypesLookup = getMessageTypesLookup();
const selectedTabKey = ref<number | null>(null);
let lastEmittedWireJson = '';
let suppressPush = false;

function normalizeSmsTypeIds(raw: unknown[] | null | undefined): number[] {
  const out: number[] = [];
  for (const value of raw ?? []) {
    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isInteger(n) && n > 0 && !out.includes(n)) out.push(n);
  }
  return out.sort((a, b) => a - b);
}

function sameSmsTypeIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((id, i) => id === b[i]);
}

function normalizeBlock(block: AggregatorSmsBillingUi): AggregatorSmsBillingUi {
  const selectedTypes = normalizeSmsTypeIds(block.selectedTypes);
  const pricesByType: Record<number, SmsTypePricesUi> = {};
  for (const smsTypeId of selectedTypes) {
    pricesByType[smsTypeId] = block.pricesByType[smsTypeId] ?? emptySmsTypePrices();
  }
  return { ...block, selectedTypes, pricesByType };
}

onMounted(() => {
  void Promise.all([aggregatorsLookup.ensureLoaded(), messageTypesLookup.ensureLoaded()]);
});

function localBlockRicherThanWire(
  local: AggregatorSmsBillingUi,
  wire: AggregatorSmsBillingUi,
): boolean {
  const wireTypes = new Set(normalizeSmsTypeIds(wire.selectedTypes));
  return normalizeSmsTypeIds(local.selectedTypes).some((t) => !wireTypes.has(t));
}

function syncUiFromWire(map: AggregatorSmsTypeMap | undefined | null): void {
  const incoming = aggregatorSmsTypeMapToUi(map);
  incoming.blocks = incoming.blocks.map(normalizeBlock);

  const incomingAggIds = new Set(incoming.blocks.map((b) => b.aggregator_id));
  const preserveLocal = ui.value.blocks.filter((local) => {
    if (!incomingAggIds.has(local.aggregator_id)) return true;
    const inc = incoming.blocks.find((b) => b.aggregator_id === local.aggregator_id);
    if (!inc) return true;
    return localBlockRicherThanWire(normalizeBlock(local), inc);
  }).map(normalizeBlock);

  const merged = new Map<number, AggregatorSmsBillingUi>();
  for (const b of incoming.blocks) merged.set(b.aggregator_id, b);
  for (const b of preserveLocal) merged.set(b.aggregator_id, b);

  suppressPush = true;
  ui.value = {
    blocks: [...merged.values()].sort((a, b) => a.aggregator_id - b.aggregator_id),
  };
  suppressPush = false;

  if (ui.value.blocks.length === 0) {
    currentBlockIndex.value = 0;
    selectedTabKey.value = null;
  } else {
    if (currentBlockIndex.value >= ui.value.blocks.length) {
      currentBlockIndex.value = ui.value.blocks.length - 1;
    }
    selectedTabKey.value = ui.value.blocks[currentBlockIndex.value]?.aggregator_id ?? null;
  }
}

watch(
  () => JSON.stringify(props.modelValue ?? {}),
  (json) => {
    if (json === lastEmittedWireJson) {
      lastEmittedWireJson = '';
      return;
    }
    syncUiFromWire(JSON.parse(json) as AggregatorSmsTypeMap);
  },
  { immediate: true },
);

const currentBlock = computed((): AggregatorSmsBillingUi | null => {
  return ui.value.blocks[currentBlockIndex.value] ?? null;
});

const blockTabItems = computed(() =>
  ui.value.blocks.map((b) => aggregatorsLookup.labelOf(b.aggregator_id)),
);

watch(selectedTabKey, (aggId) => {
  if (aggId == null) return;
  const idx = ui.value.blocks.findIndex((b) => b.aggregator_id === aggId);
  if (idx >= 0) currentBlockIndex.value = idx;
});

watch(currentBlockIndex, (idx) => {
  selectedTabKey.value = ui.value.blocks[idx]?.aggregator_id ?? null;
});

const usedAggregatorIds = computed(
  () => new Set(ui.value.blocks.map((b) => b.aggregator_id)),
);

const availableAggregators = computed(() =>
  aggregatorsLookup.items.value.filter((a) => !usedAggregatorIds.value.has(a.id)),
);

const filteredAggregatorLookup = computed(() => ({
  ...aggregatorsLookup,
  items: computed(() => availableAggregators.value),
}));

function pushUpdate(): void {
  if (suppressPush) return;
  const wire = uiToAggregatorSmsTypeMap(ui.value);
  lastEmittedWireJson = JSON.stringify(wire);
  emit('update:modelValue', wire);
}

function addAggregatorBlock(): void {
  if (newAggregatorId.value == null) return;
  if (usedAggregatorIds.value.has(newAggregatorId.value)) return;
  const aggId = newAggregatorId.value;
  ui.value.blocks.push({
    aggregator_id: aggId,
    selectedTypes: [],
    pricesByType: {},
  });
  currentBlockIndex.value = ui.value.blocks.length - 1;
  selectedTabKey.value = aggId;
  newAggregatorId.value = null;
}

function removeCurrentBlock(): void {
  ui.value.blocks.splice(currentBlockIndex.value, 1);
  currentBlockIndex.value = Math.max(0, currentBlockIndex.value - 1);
  selectedTabKey.value = ui.value.blocks[currentBlockIndex.value]?.aggregator_id ?? null;
  pushUpdate();
}

function ensureTypePrices(block: AggregatorSmsBillingUi, smsTypeId: number): SmsTypePricesUi {
  if (!block.pricesByType[smsTypeId]) {
    block.pricesByType[smsTypeId] = emptySmsTypePrices();
  }
  return block.pricesByType[smsTypeId];
}

function onTypesChanged(raw: unknown[] | null | undefined): void {
  const block = currentBlock.value;
  if (!block) return;

  const types = normalizeSmsTypeIds(raw);
  if (sameSmsTypeIds(block.selectedTypes, types)) return;

  for (const key of Object.keys(block.pricesByType)) {
    const id = Number(key);
    if (!types.includes(id)) delete block.pricesByType[id];
  }
  for (const smsTypeId of types) {
    ensureTypePrices(block, smsTypeId);
  }
  block.selectedTypes = types;
  pushUpdate();
}

function typePrices(smsTypeId: number): SmsTypePricesUi | undefined {
  return currentBlock.value?.pricesByType[smsTypeId];
}

function onStepsRemoved(smsTypeId: number): void {
  const block = currentBlock.value;
  if (!block) return;
  const entry = block.pricesByType[smsTypeId];
  if (entry && entry.steps.length === 0) {
    entry.steps.push({ from: 0, to: null, price: 0 });
  }
  pushUpdate();
}

function onSelectedTabChanged(label: string): void {
  const idx = ui.value.blocks.findIndex(
    (b) => aggregatorsLookup.labelOf(b.aggregator_id) === label,
  );
  if (idx >= 0) currentBlockIndex.value = idx;
}

const messageTypeOptions = computed(() =>
  messageTypesLookup.items.value.map((it) => ({
    value: it.id,
    text: messageTypesLookup.labelOf(it.id),
  })),
);

function messageTypeTitle(id: number): string {
  return messageTypesLookup.labelOf(id) || String(id);
}
</script>

<template>
  <div class="sms-type-billing">
    <div class="stb-add-row">
      <span class="stb-label">Добавить агрегатора:</span>
      <LookupBox
        v-model="newAggregatorId"
        :lookup="filteredAggregatorLookup as ReturnType<typeof getAggregatorsLookup>"
        placeholder="Агрегатор"
      />
      <DxButton
        icon="add"
        text="Добавить"
        type="default"
        styling-mode="contained"
        :disabled="!newAggregatorId"
        @click="addAggregatorBlock"
      />
    </div>

    <div v-if="ui.blocks.length === 0" class="stb-empty">
      Выберите агрегатора и добавьте блок цен.
    </div>

    <template v-else-if="currentBlock">
      <div class="stb-block-toolbar">
        <DxTabs
          :items="blockTabItems"
          :selected-item="blockTabItems[currentBlockIndex]"
          @update:selected-item="onSelectedTabChanged"
        />
        <DxButton
          icon="trash"
          text="Удалить агрегатора"
          styling-mode="outlined"
          @click="removeCurrentBlock"
        />
      </div>

      <div class="stb-subsection">
        <div class="stb-subsection-label">Типы SMS</div>
        <DxTagBox
          :value="currentBlock.selectedTypes"
          :items="messageTypeOptions"
          display-expr="text"
          value-expr="value"
          placeholder="Выберите типы SMS"
          :show-selection-controls="true"
          :search-enabled="true"
          @value-changed="(e) => onTypesChanged(e.value)"
        />
      </div>

      <div v-if="!currentBlock.selectedTypes.length" class="stb-empty stb-empty--compact">
        Выберите типы SMS для настройки цен.
      </div>

      <div
        v-for="smsType in currentBlock.selectedTypes"
        :key="`${currentBlock.aggregator_id}-${smsType}`"
        class="stb-type-block"
      >
        <div class="stb-type-title">{{ messageTypeTitle(smsType) }}</div>

        <div class="stb-section">
          <div class="stb-section-title">Степовая тарификация</div>
          <DxDataGrid
            v-if="typePrices(smsType)"
            :data-source="typePrices(smsType)!.steps"
            :show-borders="true"
            :show-row-lines="true"
            :hover-state-enabled="true"
            :column-auto-width="true"
            :paging="paging"
            :pager="pager"
            @row-updated="pushUpdate"
            @row-inserted="pushUpdate"
            @row-removed="onStepsRemoved(smsType)"
          >
            <DxEditing :allow-updating="true" :allow-adding="true" :allow-deleting="true" mode="cell" />
            <DxColumn data-field="from" caption="От" data-type="number" :width="110" />
            <DxColumn data-field="to" caption="До (∞ = пусто)" data-type="number" :width="130" />
            <DxColumn
              data-field="price"
              caption="Цена"
              data-type="number"
              :format="{ type: 'fixedPoint', precision: 4 }"
            />
          </DxDataGrid>
        </div>

        <div class="stb-section">
          <div class="stb-section-title">Пакетная тарификация</div>
          <DxDataGrid
            v-if="typePrices(smsType)"
            :data-source="typePrices(smsType)!.packages"
            :show-borders="true"
            :show-row-lines="true"
            :hover-state-enabled="true"
            :column-auto-width="true"
            :paging="paging"
            :pager="pager"
            @row-updated="pushUpdate"
            @row-inserted="pushUpdate"
            @row-removed="pushUpdate"
          >
            <DxEditing :allow-updating="true" :allow-adding="true" :allow-deleting="true" mode="cell" />
            <DxColumn data-field="from" caption="От" data-type="number" :width="110" />
            <DxColumn data-field="to" caption="До" data-type="number" :width="110" />
            <DxColumn
              data-field="price"
              caption="Цена"
              data-type="number"
              :format="{ type: 'fixedPoint', precision: 4 }"
            />
          </DxDataGrid>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.sms-type-billing { display: flex; flex-direction: column; gap: 16px; padding-top: 8px; }
.stb-add-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.stb-label { font-size: 13px; color: #555; }
.stb-empty { color: #9ca3af; padding: 24px; text-align: center; border: 1px dashed #e5e7eb; border-radius: 4px; }
.stb-empty--compact { padding: 12px; }
.stb-block-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.stb-type-block { display: flex; flex-direction: column; gap: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 6px; background: #fafafa; }
.stb-type-title { font-weight: 600; font-size: 14px; color: #374151; }
.stb-section { display: flex; flex-direction: column; gap: 8px; }
.stb-subsection { display: flex; flex-direction: column; gap: 6px; }
.stb-subsection-label { font-size: 12px; color: #666; }
.stb-section-title { font-weight: 500; font-size: 13px; color: #4b5563; }
</style>
